<script setup lang="ts">
  defineOptions({ name: 'CPFormItem', inheritAttrs: false })

  // Vue imports
  import { computed, type PropType } from 'vue'

  // External library imports
  import { HLFormItem } from '@platform-ui/highrise'
  import type { HLPopoverPlacement, HLPopoverTrigger } from '@platform-ui/highrise'

  // Type imports

  // Props definitions
  const props = defineProps({
    label: {
      type: String,
      required: false,
      default: '',
    },
    labelSize: {
      type: String as PropType<'sm' | 'md' | 'lg' | 'xl' | '2xl'>,
      required: false,
      default: 'sm',
    },
    path: {
      type: String,
      required: false,
      default: undefined,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    showFeedback: {
      type: Boolean,
      required: false,
      default: true,
    },
    feedback: {
      type: String,
      required: false,
      default: '',
    },
    rule: {
      type: [Object, Array] as PropType<unknown>,
      required: false,
      default: undefined,
    },
    validationStatus: {
      type: String as PropType<'success' | 'warning' | 'error'>,
      required: false,
      default: undefined,
    },
    tooltip: {
      type: Object as PropType<{
        trigger: HLPopoverTrigger
        icon?: () => unknown
        placement?: HLPopoverPlacement
        iconClass?: string
        tooltipContent: string
      }>,
      required: false,
      default: null,
    },
    showFeedbackTooltip: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    feedbackLineClamp: {
      type: Number,
      required: false,
      default: 1,
    },
    feedbackTooltipProps: {
      type: Object,
      required: false,
      default: null,
    },
    requireMarkPlacement: {
      type: String as PropType<'left' | 'right'>,
      required: false,
      default: 'right',
    },
    showRequireMark: {
      type: Boolean,
      required: false,
      default: false,
    },
  })

  // Computed properties
  const labelSizeStyles = computed(() => {
    const sizeMap = {
      sm: {
        '--n-label-font-size': '12px',
        '--n-label-height': '17px',
      },
      md: {
        '--n-label-font-size': '13px',
        '--n-label-height': '18px',
      },
      lg: {
        '--n-label-font-size': '14px',
        '--n-label-height': '20px',
      },
      xl: {
        '--n-label-font-size': '15px',
        '--n-label-height': '20px',
      },
      '2xl': {
        '--n-label-font-size': '16px',
        '--n-label-height': '24px',
      },
    }

    return {
      ...(sizeMap[props.labelSize] || sizeMap.sm),
      '--n-label-text-color': 'var(--cp-brand-font)',
      '--n-asterisk-color': 'var(--cp-error-500)',
    }
  })
</script>

<template>
  <HLFormItem
    v-bind="$attrs"
    :label="props.label"
    :path="props.path"
    :required="props.required"
    :show-feedback="props.showFeedback"
    :feedback="props.feedback"
    :rule="props.rule"
    :validation-status="props.validationStatus"
    :tooltip="props.tooltip"
    :show-feedback-tooltip="props.showFeedbackTooltip"
    :feedback-line-clamp="props.feedbackLineClamp"
    :feedback-tooltip-props="props.feedbackTooltipProps"
    :require-mark-placement="props.requireMarkPlacement"
    :show-require-mark="props.showRequireMark"
    :style="labelSizeStyles"
  >
    <slot />
    <template v-if="$slots.feedback" #feedback>
      <slot name="feedback" />
    </template>
    <template v-if="$slots.label" #label>
      <slot name="label" />
    </template>
  </HLFormItem>
</template>

<!--
  HighRise form labels inherit --gray-900 (#101828) which stays navy in dark
  mode. Unscoped so the override reaches HLFormItem's inner label nodes;
  !important beats HighRise's .hr-v-3-7-0 + data-v specificity.
-->
<style>
  .hr-form-item,
  .n-form-item {
    --n-label-text-color: var(--cp-brand-font) !important;
    --n-text-color: var(--cp-brand-font) !important;
  }

  .hr-form-item-label,
  .hr-form-item-label__text,
  .n-form-item-label {
    color: var(--cp-brand-font) !important;
  }

  .hr-form-item-blank--error .hr-input,
  .hr-form-item-blank--error .hr-base-selection,
  .hr-form-item-blank--error .hr-time-picker,
  .hr-form-item-blank--error .hr-date-picker {
    --n-color: var(--cp-background-500) !important;
    --n-color-focus: var(--cp-background-500) !important;
    --n-color-active: var(--cp-background-500) !important;
    background-color: var(--cp-background-500) !important;
  }
</style>
